import type { ReactNode } from 'react';
import { getLandingPricingPlanTitleKey } from '../../utils/landingPricingPlans';
import { useTranslation } from '../../contexts/TranslationContext';
import type { KnowledgeLawCountry } from '../../services/knowledgeLawCountries';
import type { BillingSubscriptionProduct } from '../../types/billing';
import { formatBillingAmount, replaceBillingTranslationValues } from '../../utils/billingPricing';
import { getBillingSubscriptionPlan } from '../../utils/billingSubscriptionPlans';
import { ArrowRight, Loader2, Lock } from '../Icons';
import { FadeSwitch } from '../FadeSwitch';
import { BrandButton } from '../ui/BrandButton';
import { SignalTag } from '../ui/SignalTag';
import { PricingFeatureList } from './PricingFeatureList';
import { PricingCard, PricingCardHeader } from './PricingCard';

export function PaidPricingCard({ product, countries, onSelect, onPlanIntent, compact = false, controls, isSubmitting = false, monthsSaved = 0 }: {
  product: BillingSubscriptionProduct;
  countries: KnowledgeLawCountry[];
  onSelect: () => void;
  onPlanIntent?: () => void;
  compact?: boolean;
  controls?: ReactNode;
  isSubmitting?: boolean;
  monthsSaved?: number;
}) {
  const { t, language } = useTranslation();
  const plan = getBillingSubscriptionPlan(product);
  const featurePlan = plan === 'pro' ? 'standard' : plan;
  return (
    <PricingCard plan={featurePlan} onSelect={() => { if (!isSubmitting) onSelect(); }} highlighted={plan === 'pro' || compact} compact={compact}>
      {!compact && <div className="mb-3 flex min-h-8 items-center justify-center">
        {plan === 'pro' && <SignalTag tone="rating" className="px-3 py-1 text-xs font-semibold">
          {t('billing.landing.bestSeller')}
        </SignalTag>}
      </div>}
      <PricingCardHeader compact={compact} controls={controls} title={t(`billing.landing.${getLandingPricingPlanTitleKey(plan)}`)} price={
          <p key={product.priceKey} className={`adr-quiz-score-update max-w-full break-words font-bold leading-none text-orange-100 ${compact ? 'text-3xl' : 'text-5xl sm:text-6xl'}`}>
            {formatBillingAmount(product.interval === 'annual' ? product.amount / 12 : product.amount, product.currency, language)}
          </p>
      } period={
          <p className="text-base font-semibold text-slate-300 sm:text-lg">
            {t('billing.pack.taxExcluded')} / {t('billing.landing.month')}
          </p>
      } detail={
          <FadeSwitch viewKey={product.interval} className="relative min-h-5 w-full" childClassName="min-h-5">
            {product.interval === 'annual' && (
              <p className="text-sm font-bold text-slate-400">
                {replaceBillingTranslationValues(t('billing.landing.billedAnnually'), { price: formatBillingAmount(product.amount, product.currency, language) })}
              </p>
            )}
          </FadeSwitch>
      } />
      <FadeSwitch viewKey={`${product.interval}-${monthsSaved}`} className="relative min-h-7 w-full" childClassName="min-h-7">
        {product.interval === 'annual' && monthsSaved > 0 && <SignalTag tone="secure" className="px-3 py-1 text-xs font-semibold">
          {replaceBillingTranslationValues(t('billing.landing.monthsSaved'), { count: String(monthsSaved) })}
        </SignalTag>}
      </FadeSwitch>
      <PricingFeatureList plan={featurePlan} countries={countries} includedOnly={compact} compact={compact} />
      <div className={`w-full ${compact ? 'mt-5' : 'mt-auto pt-8'}`}>
        {!compact && <div className="mb-3 flex justify-center">
          <SignalTag tone="secure" className="px-3 py-1 text-xs font-semibold" icon={<Lock className="h-4 w-4 shrink-0" aria-hidden="true" />}>
            {t('billing.landing.securePaymentTag')}
          </SignalTag>
        </div>}
        {!compact && <p className="mb-2 text-[11px] font-medium text-slate-500 sm:text-xs">{t('billing.landing.cancellationReminder')}</p>}
        <BrandButton variant="primary" size="lg" className="min-h-12 w-full justify-center" disabled={isSubmitting} aria-busy={isSubmitting} onMouseEnter={onPlanIntent} onFocus={onPlanIntent} onClick={onSelect}>
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          {t(plan === 'enterprise' ? 'billing.landing.selectEnterprise' : 'billing.landing.select')}
          <ArrowRight className="h-4 w-4" />
        </BrandButton>
      </div>
    </PricingCard>
  );
}
